import React from 'react'
import styled from '@emotion/styled'
import {
  KB,
  Chat,
  Chart,
  MySettings,
  Inbox,
} from '@groovehq/internal-design-system/lib/assets/icons'
import GlobalNav from '@groovehq/internal-design-system/lib/components/GlobalNav/GlobalNav'
import ProgressCircle from '@groovehq/internal-design-system/lib/components/ProgressCircle/ProgressCircle'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import { divider } from '@groovehq/internal-design-system/lib/styles/elements'
import logoChristmas from '@groovehq/internal-design-system/lib/assets/images/lettermark-chrismas.png'
import logo from 'assets/lettermark.svg'
import {
  chatsNew as ChatV2,
  emailNew as InboxV2,
  social as Social,
} from 'assets/icons/groove/v2'
import storage from 'util/storage'
import { propFunc } from 'util/functions'

import { doOpenMainPage } from 'actions/pages'
import {
  CHAT_PAGE as CHAT_LANDING_PAGE,
  SOCIAL_PAGE as SOCIAL_LANDING_PAGE,
} from 'subapps/chat/pages'
import { LANDING_PAGE as KB_LANDING_PAGE } from 'subapps/kb/pages'
import { REPORTS_MAIN_PAGE } from 'ducks/reports/pages'
import AppsMenu from 'components/AppsMenu'

import GlobalNavItem from './GlobalNavItem'
import SettingsMenu from './SettingsMenu'
import HelpItem from './HelpItem'
import { styles } from './styles'

const StyledUserItem = styled(SettingsMenu)`
  margin-top: 8px;
  margin-left: 8px;
  margin-right: 8px;
`

// Javascript with 0 based months...wtf
// so 11 actually means 12 here
const isDecember = new Date().getMonth() === 11

const currentLogo = isDecember ? logoChristmas : logo

const toChatLandingPage = { type: CHAT_LANDING_PAGE }
const toSocialLandingPage = { type: SOCIAL_LANDING_PAGE }
const toKbLandingPage = { type: KB_LANDING_PAGE }
const toReportsLandingPage = { type: REPORTS_MAIN_PAGE }

const appsMenuOffset = [50, -48]

export default class GlobalNavWrapper extends React.Component {
  state = { visible: false }

  componentDidMount() {
    const { setLeftSidebarOpenState, active } = this.props
    const isOpen = this.getLeftSidebarState(active)
    setLeftSidebarOpenState(isOpen)
  }

  getLeftSidebarState = subapp => {
    const storedStates = storage.get('leftSidebarOpenStates')
    return storedStates && storedStates[subapp] !== undefined
      ? storedStates[subapp]
      : true
  }

  rememberLeftSidebarState = subapp => {
    const { isLeftSidebarCollapsed } = this.props
    const storedStates = storage.get('leftSidebarOpenStates')

    storage.set('leftSidebarOpenStates', {
      ...storedStates,
      [subapp]: !!isLeftSidebarCollapsed,
    })
  }

  handleSubappClick = subapp => () => {
    const { setLeftSidebarOpenState } = this.props
    const isOpen = this.getLeftSidebarState(subapp)
    setLeftSidebarOpenState(isOpen)
  }

  handleVisibleChange = isVisible => {
    const { hasSeenInstallAppMenu, setSeenInstallAppMenu } = this.props
    this.setState({ visible: isVisible })
    if (isVisible && hasSeenInstallAppMenu === false) {
      setSeenInstallAppMenu()
    }
  }

  render() {
    const {
      active,
      noticeCounts,
      alerts,
      isLeftSidebarCollapsed,
      isChatEnabled,
      isSocialEnabled,
      settingsLandingPageAction,
      toggleLeftSidebar,
      billingProvider,
      hasSeenInstallAppMenu,
      isCurrentUserSignupOlderThan2Weeks,
      showMobileAppsMenu,
      showReporting,
      showTopNav,
    } = this.props

    const showNav = isChatEnabled || isSocialEnabled

    const showPhoneIcon =
      showMobileAppsMenu && isCurrentUserSignupOlderThan2Weeks

    const globalNavWithoutTopNavProps = {
      isLeftSidebarCollapsed,
      doToggleLeftSidebar: toggleLeftSidebar,
      onClick: propFunc(this.rememberLeftSidebarState, active),
      logo: currentLogo,
      logoStyles: isDecember ? styles.logo : undefined,
      onClickMenuButton: propFunc(this.rememberLeftSidebarState, active),
    }

    const globalNavWithTopNavProps = {
      className: 'grui pt-4',
    }

    if (!showNav) return null

    return (
      <GlobalNav
        {...(showTopNav
          ? globalNavWithTopNavProps
          : globalNavWithoutTopNavProps)}
        metaNav={
          <>
            {showPhoneIcon && (
              <Dropdown
                overlay={
                  <AppsMenu
                    onVisibleChange={this.handleVisibleChange}
                    showHideButton
                  />
                }
                autoHeight
                position="top-start"
                strategy="fixed"
                offset={appsMenuOffset}
                visible={this.state.visible}
                onVisibleChange={this.handleVisibleChange}
              >
                <div className="grui position-relative">
                  <GlobalNavItem
                    title="Get the Groove mobile app!"
                    icon={<div css={styles.phone}>📱</div>}
                  />
                  {hasSeenInstallAppMenu === false && (
                    <ProgressCircle css={styles.circle} />
                  )}
                </div>
              </Dropdown>
            )}
            <div css={divider.styles.primary} role="separator" />
            {!showTopNav && (
              <>
                <HelpItem isLeftSidebarCollapsed={isLeftSidebarCollapsed} />
                <StyledUserItem />
              </>
            )}
          </>
        }
      >
        <GlobalNavItem
          to={doOpenMainPage()}
          title={app.t('Mailbox')}
          icon={showTopNav ? <InboxV2 /> : <Inbox />}
          isActive={active === 'Inbox'}
          onClick={propFunc(this.handleSubappClick, 'Inbox')}
          noticeCount={noticeCounts.Inbox || null}
          alert={alerts.Inbox || null}
        />
        {isChatEnabled &&
          billingProvider !== 'shopify' && (
            <GlobalNavItem
              to={toChatLandingPage}
              title="Live Chat"
              icon={showTopNav ? <ChatV2 /> : <Chat />}
              isActive={active === 'Live Chat'}
              onClick={propFunc(this.handleSubappClick, 'Live Chat')}
              noticeCount={noticeCounts['Live Chat'] || null}
              alert={alerts.widget || null}
            />
          )}
        {isSocialEnabled &&
          billingProvider !== 'shopify' && (
            <GlobalNavItem
              to={toSocialLandingPage}
              title="Social Chat"
              icon={<Social />}
              isActive={active === 'Social Chat'}
              isLeftSidebarCollapsed={isLeftSidebarCollapsed}
              onClick={propFunc(this.handleSubappClick, 'Social Chat')}
              noticeCount={noticeCounts['Social Chat'] || null}
              alert={alerts.social || null}
              css={styles.navItemWithBigIcon}
            />
          )}
        {!showTopNav && (
          <>
            <GlobalNavItem
              to={toKbLandingPage}
              title="Knowledge Base"
              icon={<KB />}
              isActive={active === 'Knowledge Base'}
              isLeftSidebarCollapsed={isLeftSidebarCollapsed}
              onClick={propFunc(this.handleSubappClick, 'Knowledge Base')}
              noticeCount={noticeCounts['Knowledge Base'] || null}
              alert={alerts['Knowledge Base'] || null}
            />
            {showReporting && (
              <GlobalNavItem
                to={toReportsLandingPage}
                title="Reporting"
                icon={<Chart />}
                isActive={active === 'Reporting'}
                isLeftSidebarCollapsed={isLeftSidebarCollapsed}
                onClick={propFunc(this.handleSubappClick, 'Reporting')}
                noticeCount={noticeCounts.Reporting || null}
                alert={alerts.Reporting || null}
              />
            )}
            <GlobalNavItem
              to={settingsLandingPageAction}
              title="Settings"
              icon={<MySettings />}
              isActive={active === 'Settings'}
              isLeftSidebarCollapsed={isLeftSidebarCollapsed}
              onClick={propFunc(this.handleSubappClick, 'Settings')}
              noticeCount={noticeCounts.Settings || null}
              featureDotFlag="showV2SettingsFeatureDot"
            />
          </>
        )}
      </GlobalNav>
    )
  }
}
